import { useMemo } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Area, CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer,
  Tooltip as ChartTooltip, XAxis, YAxis,
} from "recharts";
import { Download, ExternalLink, LineChart as LineChartIcon, Users } from "lucide-react";
import { formatJamaicaDate } from "@/lib/datetime";
import {
  GROUP_LABELS, GROUP_ORDER, formatMetricShort, formatMetricValue, formatMoney, getDrilldown,
  getMetricCatalogue, getOverview, getSeries, metricsUrls, previousRange, shortDate,
  type DrilldownResponse, type Granularity, type MetricDef, type MetricUnit, type MetricsCatalogue,
  type OverviewResponse, type ScopedParams, type SeriesResponse,
} from "@/lib/metrics";
import { ChangeChip, EmptyState, ErrorState, StatusPill, TableSkeleton, Tile } from "./shared";

const FALLBACK_UNIT: MetricUnit = "count";

export default function ExploreTab({
  params, compare, metricId, onSelectMetric, granularity, onSelectGranularity, onOpenCustomer,
}: {
  params: ScopedParams;
  compare: boolean;
  metricId: string;
  onSelectMetric: (id: string) => void;
  granularity: Granularity | "auto";
  onSelectGranularity: (g: Granularity | "auto") => void;
  onOpenCustomer: (customerId: string) => void;
}) {
  const { data: catalogue } = useQuery<MetricsCatalogue>({
    queryKey: [metricsUrls.metrics()],
    queryFn: getMetricCatalogue,
  });

  const def: MetricDef | undefined = catalogue?.metrics.find((m) => m.id === metricId);
  const unit: MetricUnit = def?.unit || FALLBACK_UNIT;

  const seriesParams = { ...params, metric: metricId, granularity };
  const { data: series, isLoading, error } = useQuery<SeriesResponse>({
    queryKey: [metricsUrls.series(seriesParams)],
    queryFn: () => getSeries(seriesParams),
  });

  const prev = previousRange(params.from, params.to);
  const prevParams = { ...params, ...prev, metric: metricId, granularity };
  const { data: prevSeries } = useQuery<SeriesResponse>({
    queryKey: [metricsUrls.series(prevParams)],
    queryFn: () => getSeries(prevParams),
    enabled: compare,
  });

  // Reuses the Control Center's cached response when it is already loaded.
  const { data: overview } = useQuery<OverviewResponse>({
    queryKey: [metricsUrls.overview({ ...params, compare })],
    queryFn: () => getOverview({ ...params, compare }),
  });
  const card = overview?.cards.find((c) => c.id === metricId);

  const drillParams = { ...params, metric: metricId };
  const { data: drill, isLoading: drillLoading, error: drillError } = useQuery<DrilldownResponse>({
    queryKey: [metricsUrls.drilldown(drillParams)],
    queryFn: () => getDrilldown(drillParams),
  });

  const points = series?.points || [];
  const chartData = useMemo(() => points.map((p, i) => ({
    date: p.date,
    label: p.label,
    value: p.value,
    previous: compare ? prevSeries?.points?.[i]?.value ?? null : null,
    previousLabel: prevSeries?.points?.[i]?.label ?? null,
  })), [points, prevSeries, compare]);

  // Snap each annotation onto the bucket it falls inside.
  const markers = useMemo(() => {
    const anns = series?.annotations || [];
    return anns.map((a) => {
      let bucket = points[0]?.date;
      for (const p of points) if (p.date <= a.date) bucket = p.date;
      return bucket && a.date >= (points[0]?.date ?? "") ? { ...a, bucket } : null;
    }).filter(Boolean) as Array<{ id: string; title: string; bucket: string; kind: string }>;
  }, [series, points]);

  const kind = (def?.kind || "").toLowerCase();
  const fallbackCurrent = points.length === 0 ? null
    : kind === "stock" || kind === "level" ? points[points.length - 1].value
      : points.reduce((a, p) => a + p.value, 0);
  const prevPoints = prevSeries?.points || [];
  const fallbackPrevious = prevPoints.length === 0 ? null
    : kind === "stock" || kind === "level" ? prevPoints[prevPoints.length - 1].value
      : prevPoints.reduce((a, p) => a + p.value, 0);

  const currentValue = card?.value ?? fallbackCurrent;
  const previousValue = card?.previous ?? fallbackPrevious;
  const change = card?.change ?? (currentValue != null && previousValue != null ? currentValue - previousValue : null);
  const changePct = card?.changePct ?? (change != null && previousValue ? (change / Math.abs(previousValue)) * 100 : null);

  const csvUrl = metricsUrls.exportCsv(seriesParams);

  const grouped = GROUP_ORDER
    .map((g) => ({ group: g, metrics: (catalogue?.metrics || []).filter((m) => m.group === g) }))
    .filter((g) => g.metrics.length > 0);

  return (
    <div className="space-y-6" data-testid="tab-explore">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[15rem]">
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Metric</Label>
                <Select value={metricId} onValueChange={onSelectMetric}>
                  <SelectTrigger className="h-9" data-testid="select-metric">
                    <LineChartIcon className="w-3.5 h-3.5 mr-1 shrink-0 opacity-60" />
                    <SelectValue placeholder="Pick a metric" />
                  </SelectTrigger>
                  <SelectContent className="max-h-96">
                    {grouped.length === 0 ? (
                      <SelectItem value={metricId}>{metricId}</SelectItem>
                    ) : grouped.map(({ group, metrics }) => (
                      <SelectGroup key={group}>
                        <SelectLabel>{GROUP_LABELS[group]}</SelectLabel>
                        {metrics.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Granularity</Label>
                <Tabs value={granularity} onValueChange={(v) => onSelectGranularity(v as Granularity | "auto")}>
                  <TabsList className="h-9">
                    <TabsTrigger value="auto" data-testid="granularity-auto">Auto</TabsTrigger>
                    <TabsTrigger value="day">Day</TabsTrigger>
                    <TabsTrigger value="week">Week</TabsTrigger>
                    <TabsTrigger value="month">Month</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </div>
            <Button variant="outline" size="sm" asChild data-testid="button-export-csv">
              <a href={csvUrl} download>
                <Download className="w-4 h-4 mr-1" />Export CSV
              </a>
            </Button>
          </div>
          {def?.description ? <CardDescription className="pt-2">{def.description}</CardDescription> : null}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Tile label="This period" value={formatMetricValue(currentValue, unit)} sub={`${shortDate(params.from)} – ${shortDate(params.to)}`} testId="tile-current" />
            <Tile label="Previous period" value={formatMetricValue(previousValue, unit)} sub={`${shortDate(prev.from)} – ${shortDate(prev.to)}`} testId="tile-previous" />
            <div className="rounded-lg border bg-card p-3">
              <div className="text-xs text-muted-foreground">Change</div>
              <div className="mt-2"><ChangeChip metricId={metricId} change={change} changePct={changePct} unit={unit} /></div>
              <div className="text-xs text-muted-foreground mt-1.5">
                {series?.granularity ? `Bucketed by ${series.granularity}` : ""}
              </div>
            </div>
            <Tile
              label="Data points"
              value={String(points.length)}
              sub={markers.length ? `${markers.length} annotation${markers.length === 1 ? "" : "s"} in range` : "No annotations in range"}
            />
          </div>

          {isLoading ? (
            <div className="h-80 flex items-center justify-center"><TableSkeleton rows={4} cols={6} /></div>
          ) : error ? (
            <ErrorState error={error} />
          ) : points.length === 0 ? (
            <EmptyState title="No data for this metric in this period" hint="Try a wider period, clear the segment filters, or pick another metric." />
          ) : (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="metricFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={20} />
                  <YAxis tick={{ fontSize: 11 }} width={70} tickFormatter={(v: number) => formatMetricShort(v, unit)} />
                  <ChartTooltip
                    formatter={(v: any, n: any) => [v == null ? "–" : formatMetricValue(Number(v), unit), n === "previous" ? "Previous period" : def?.label || metricId]}
                    labelFormatter={(l: any, p: any) => {
                      const row = p?.[0]?.payload;
                      return row?.previousLabel ? `${l} (vs ${row.previousLabel})` : l;
                    }}
                  />
                  {compare && <Legend />}
                  <Area type="monotone" dataKey="value" name={def?.label || metricId} stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#metricFill)" isAnimationActive={false} />
                  {compare && (
                    <Line type="monotone" dataKey="previous" name="previous" stroke="hsl(var(--muted-foreground))" strokeWidth={1.5} strokeDasharray="4 3" dot={false} isAnimationActive={false} />
                  )}
                  {markers.map((m) => {
                    const label = chartData.find((d) => d.date === m.bucket)?.label;
                    if (!label) return null;
                    return (
                      <ReferenceLine
                        key={m.id}
                        x={label}
                        stroke="hsl(var(--muted-foreground))"
                        strokeDasharray="2 2"
                        label={{ value: m.title, fontSize: 10, position: "top", fill: "hsl(var(--muted-foreground))" }}
                      />
                    );
                  })}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}

          {markers.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {markers.map((m) => (
                <Badge key={m.id} variant="outline" className="font-normal text-[11px]">{shortDate(m.bucket)} · {m.title}</Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" />Who is behind this number</CardTitle>
          <CardDescription>Every customer that moved {def?.label || metricId} between {shortDate(params.from)} and {shortDate(params.to)}.</CardDescription>
        </CardHeader>
        <CardContent>
          {drillLoading ? <TableSkeleton rows={6} cols={7} />
            : drillError ? <ErrorState error={drillError} />
              : !drill || drill.rows.length === 0 ? (
                <EmptyState title="Nobody moved this metric in this period" hint="Either nothing happened, or the segment you picked has no customers in it." />
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead>Grade</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">MRR</TableHead>
                        <TableHead>When</TableHead>
                        <TableHead className="text-right">Change</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead className="w-24" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {drill.rows.map((row, i) => (
                        <TableRow key={`${row.customerId}-${row.at}-${i}`} data-testid={`drilldown-row-${i}`}>
                          <TableCell className="max-w-[16rem]">
                            <button
                              type="button"
                              className="font-medium text-primary hover:underline text-left truncate block"
                              onClick={() => onOpenCustomer(row.customerId)}
                            >
                              {row.name || row.email || row.customerId}
                            </button>
                            <div className="text-xs text-muted-foreground truncate">{row.email || "–"}</div>
                          </TableCell>
                          <TableCell>{row.grade != null ? <Badge variant="outline" className="text-[10px] py-0 h-4">G{row.grade}</Badge> : "–"}</TableCell>
                          <TableCell className="capitalize text-sm">{row.plan || "–"}{row.interval ? <span className="text-xs text-muted-foreground"> / {row.interval}</span> : null}</TableCell>
                          <TableCell><StatusPill status={row.status} /></TableCell>
                          <TableCell className="text-right tabular-nums">{row.mrr != null ? formatMoney(row.mrr) : "–"}</TableCell>
                          <TableCell className="whitespace-nowrap text-sm">{formatJamaicaDate(row.at)}</TableCell>
                          <TableCell className={`text-right tabular-nums ${((row.delta ?? row.amount ?? 0) < 0) ? "text-destructive" : ""}`}>
                            {row.delta != null ? formatMoney(row.delta) : row.amount != null ? formatMoney(row.amount) : "–"}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-[14rem] truncate">{row.reason || row.type || "–"}</TableCell>
                          <TableCell className="text-right">
                            {row.userId ? (
                              <Link href={`/admin/users?openUser=${row.userId}`} className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                                Open user <ExternalLink className="w-3 h-3" />
                              </Link>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
        </CardContent>
      </Card>
    </div>
  );
}
